import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import Banner from "./sections/PageBanner";
import PageHeader from "./sections/PageHeader";
import Footer from "./sections/PageFooter";

const faqs = [
  {
    question: "What is WITS?",
    answer:
      "WITS stands for Wildlife Information & Tracking System. It is a secure, data-driven platform that collects, tracks, and analyzes wildlife-related incidents, including poaching, habitat loss, illegal trade, and human-wildlife conflict to support evidence-based conservation efforts.",
  },
  {
    question: "Why was WITS created?",
    answer:
      "WITS was created to change the narrative of conservation from one of anecdotal effort to evidence-based action, measured impact, and collective intelligence. Through this transformation, conservation becomes not only more accountable and efficient but also more inclusive and community-driven, laying the groundwork for long-term biodiversity protection.",
  },
  {
    question: "Who can use the WITS platform?",
    answer:
      "Anyone can use WITS—community members, conservationists, government officials, NGOs, researchers, and volunteers. We welcome both anonymous and verified users.",
  },
  {
    "question": "What kinds of incidents can I report?",
    "answer": "You can report:\n\nA. Wildlife Crimes:\n- Poaching of endangered species (e.g., elephants, rhinos, pangolins)\n- Illegal possession or trade of wildlife parts (e.g., ivory, skins, trophies)\n- Trafficking of live animals for exotic pet trade\n- Bushmeat hunting and trading\n- Retaliatory killings due to human-wildlife conflict\n- Use of banned traps, snares, or poison for hunting\n- Violation of protected area laws (e.g., unauthorized hunting in national parks)\n\nB. Forestry and Land Crimes:\n- Illegal logging and timber harvesting\n- Charcoal production in protected areas\n- Encroachment into forest reserves\n- Destruction of mangroves or wetlands\n- Land grabbing within protected ecosystems\n\nC. Fisheries and Marine Crimes:\n- Illegal, unreported, and unregulated (IUU) fishing\n- Use of explosives or poisons in fishing\n- Fishing in breeding grounds or marine protected areas\n- Bycatch of protected marine species (e.g., turtles, dolphins)\n- Import/export of endangered marine species without permits\n\nD. Pollution and Waste Crimes:\n- Dumping of hazardous waste into rivers, lakes, or oceans\n- Air pollution from unlicensed industrial activities\n- Oil spills or toxic discharge from vessels\n- Open burning of waste\n- Unregulated disposal of plastics or e-waste\n\nE. Climate and Resource-Based Crimes:\n- Mining in protected areas\n- Sand harvesting without permits\n- Destruction of carbon sinks (e.g., peatlands, wetlands)\n- Violation of climate or emissions regulations\n- Unauthorized drilling or extraction of natural resources\n\nF. Related Governance and Enforcement Crimes:\n- Corruption in granting of environmental permits\n- Tampering with environmental impact assessments (EIAs)\n- Bribery or intimidation to avoid enforcement\n- Forgery of CITES or export/import permits\n- Obstruction of environmental or wildlife law enforcement"
  },
  {
    "question": "Can I report illegal bushmeat trade?",
    "answer": "Yes, wildlife trafficking of any kind, including bushmeat trade, is within WITS’ scope."
  },
  {
    "question": "Can WITS track retaliatory killings of wildlife?",
    "answer": "Yes, users can report retaliatory killings under the “Human-Wildlife Conflict” category."
  },
  {
    "question": "Are habitat encroachments reportable?",
    "answer": "Yes, illegal settlement, logging, or farming in protected areas can be reported."
  },
  {
  "question": "Can I report wildlife poisoning cases?",
  "answer": "Yes, poisoning, snaring, or any harm to wildlife is reportable."
},
{
  "question": "Can I track the status of my report?",
  "answer": "Yes, once submitted, you can log in to view status updates (under review, verified, acted upon)."
},
{
  "question": "Can I view incidents on a map?",
  "answer": "Yes, WITS features a GIS-enabled dashboard that shows spatial incident data."
},
{
  "question": "Can I access statistics or download data?",
  "answer": "Yes, limited aggregated data is publicly available; detailed data may require access approval."
},
{
  "question": "How is data verified?",
  "answer": "Reports go through a validation process that may include field checks, cross-referencing, and expert review."
},
{
  "question": "Can I use WITS data for research?",
  "answer": "Yes, upon approval and subject to data sharing agreements."
},
{
  "question": "Can organizations integrate their data into WITS?",
  "answer": "Yes, WITS supports integration with existing data systems via secure APIs."
},
{
  "question": "Is it safe to report incidents on WITS?",
  "answer": "Yes. WITS has a strict Whistleblower Protection Policy. All reports are confidential, and you can remain anonymous. We use encryption and secure data handling to protect your identity and submissions."
},
{
  "question": "Can I report anonymously?",
  "answer": "Yes, users can choose to report anonymously or opt to share their identity for follow-up."
},
{
  "question": "Who can access the data I submit?",
  "answer": "Data is accessed only by authorized personnel or organizations, depending on your privacy settings."
},
{
  "question": "How does WITS ensure data is secure?",
  "answer": "WITS uses encryption, secure servers, and blockchain technology to protect data integrity and user privacy."
},
{
  "question": "How do I report an incident on WITS?",
  "answer": "Log in, select “Report Incident,” fill in the required details, attach media if available, and submit."
},
{
  "question": "Is there a mobile version of WITS?",
  "answer": "Yes, WITS is mobile-friendly and will soon have an Android and iOS app."
},
{
  "question": "Can I attach photos or videos with my report?",
  "answer": "Yes, you can upload visual evidence, which is highly encouraged for validation."
},
{
  "question": "Can I edit my report after submission?",
  "answer": "No. Once submitted, reports are reviewed by the WITS admin team. However, you can submit additional information as a follow-up."
},
{
  "question": "Is the platform free to use?",
  "answer": "Yes, WITS is free for individuals and non-profit conservation use."
},
{
  "question": "How do I submit a report?",
  "answer": "You can report through the WITS web portal, mobile app, or through authorized community partners. Each submission goes through a secure and verified process."
},
{
  "question": "Is WITS only for professionals?",
  "answer": "No, anyone from community members to researchers can report or access relevant features depending on their user access level."
},
{
  "question": "Can I upload photos or videos as evidence?",
  "answer": "Yes, users are encouraged to upload images, videos, and location data to support their reports. This helps improve verification and response accuracy."
},
{
  "question": "What happens after I submit a report?",
  "answer": "Each report is verified by WITS moderators or partner organizations. Verified data contributes to a central conservation database and may trigger alerts for urgent response or legal follow-up."
},
{
  "question": "Will I be contacted after making a report?",
  "answer": "Only if you choose to provide your contact details. Anonymous reporters will not be contacted. All communication is handled securely and respectfully."
},
{
  "question": "How is the data used?",
  "answer": "WITS data supports conservation planning, law enforcement, policy-making, and public awareness. It informs real-time response and long-term strategies to protect biodiversity."
},
{
  "question": "Is WITS open-source?",
  "answer": "Yes, WITS is designed as an open-source platform to foster transparency, collaboration, and innovation in wildlife conservation."
},
{
  "question": "Can I view incidents on a map?",
  "answer": "Yes, WITS features a GIS-enabled dashboard that shows spatial incident data."
},
{
  "question": "Can I access statistics or download data?",
  "answer": "Yes, limited aggregated data is publicly available; detailed data may require access approval."
},
{
  "question": "How is data verified?",
  "answer": "Reports go through a validation process that may include field checks, cross-referencing, and expert review."
},
{
  "question": "Can I use WITS data for research?",
  "answer": "Yes, upon approval and subject to data sharing agreements."
},
{
  "question": "Can organizations integrate their data into WITS?",
  "answer": "Yes, WITS supports integration with existing data systems via secure APIs."
},
{
  "question": "How do communities benefit from WITS?",
  "answer": "Communities gain access to conservation data, can report conflicts, and participate in citizen science efforts."
},
{
  "question": "How can rangers use WITS?",
  "answer": "Rangers can log poaching incidents, patrol data, and habitat disturbances in real time."
},
{
  "question": "Is WITS available in local languages?",
  "answer": "Yes, the platform is being translated into Swahili and other regional languages."
},
{
  "question": "Can WITS be used offline?",
  "answer": "An offline data collection tool is in development for areas without network coverage."
},
{
  "question": "How can my community start using WITS?",
  "answer": "Contact the WITS outreach team for onboarding and training."
},
{
  "question": "How can I volunteer with WITS?",
  "answer": "You can sign up via the “Get Involved” section for roles in reporting, outreach, or technical support."
},
{
  "question": "Can my organization partner with WITS?",
  "answer": "Yes. We welcome partnerships with conservation NGOs, government agencies, and research institutions."
},
{
  "question": "Is training provided for volunteers?",
  "answer": "Yes, volunteers receive basic training on using WITS and wildlife incident identification."
},
{
  "question": "How can schools use WITS?",
  "answer": "Schools can incorporate WITS into environmental clubs or as a learning tool for citizen science."
},
{
  "question": "Can I intern with WITS?",
  "answer": "Yes. Internship opportunities are posted on the WITS website."
},
{
  "question": "What technologies power WITS?",
  "answer": "WITS integrates GIS mapping, AI, machine learning, and blockchain for secure and intelligent data analysis."
},
{
  "question": "Is WITS open-source?",
  "answer": "Yes, core components of WITS are open-source to encourage transparency and collaboration."
},
{
  "question": "Can I contribute to WITS as a developer?",
  "answer": "Absolutely. Contact our tech team or contribute via GitHub."
},
{
  "question": "Is WITS scalable to other countries?",
  "answer": "Yes, the platform is designed for scalability and can be localized in any region."
},
{
  "question": "How does WITS support conservation policy?",
  "answer": "WITS provides real-time, verified data that policymakers can use to craft targeted wildlife protection laws and strategies."
}

];

export default function FAQs() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
    <Banner />
    <PageHeader />
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-3xl font-bold text-green-700 mb-6">Frequently Asked Questions</h2>
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={index} className="border border-green-300 rounded-2xl overflow-hidden shadow-sm">
            <button
              className="w-full flex justify-between items-center bg-green-100 px-6 py-4 text-left text-green-800 font-medium hover:bg-green-200 focus:outline-none"
              onClick={() => toggleFAQ(index)}
            >
              <span>{faq.question}</span>
              {openIndex === index ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {openIndex === index && (
              <div className="px-6 py-4 bg-white text-green-900 border-t border-green-200">
                <p className="whitespace-pre-line">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
    <Footer />
    </>
  );
}
